/**
 * MessagesPage — Conversation list with Chats/Channels tabs
 *
 * Split into two views:
 * - MobileMessagesView: Regular page (renders in AppLayout <main>, bottom nav visible)
 *   Uses navigate() for routing — outside Stackflow tree.
 * - DesktopMessagesView: Welcome placeholder (Stackflow left pane handles list)
 *   Uses useMessagingFlow() + registerMessagingFlow.
 */

import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { useActivity } from '@stackflow/react';
import { useAuthStore } from '@/features/auth';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import {
  useConversations,
  useChannels,
  useLeaveConversation,
  useTotalUnreadCount,
  useMarkAllAsRead,
  useBatchPresence,
  getConversationDisplayName,
  getConversationAvatar,
} from '../hooks';
import { useConversationTyping } from '../hooks';
import { useMessagingFlow } from '@/stackflow';
import {
  registerMessagingFlow,
  unregisterMessagingFlow,
  setPendingPush,
} from '../messagingNavBridge';
import { messagingApi } from '../api';
import { ChatAvatar } from '../components';
import type { Conversation, Channel } from '../types';

// =============================================================================
// Mobile Conversation Item — Brand-colored card design
// =============================================================================

interface ConversationItemProps {
  conversation: Conversation;
  currentUserId: string;
  isOnline: boolean;
  onClick: () => void;
  onDelete?: (conversationId: string) => void;
}

const ConversationItem = memo(function ConversationItem({
  conversation,
  currentUserId,
  isOnline,
  onClick,
  onDelete,
}: ConversationItemProps) {
  const { t, i18n } = useTranslation();
  const typingUsers = useConversationTyping(conversation.id);
  const name = getConversationDisplayName(conversation, currentUserId);
  const avatar = getConversationAvatar(conversation, currentUserId);
  const hasUnread = conversation.unreadCount > 0;

  const timeLabel = useMemo(() => {
    if (!conversation.lastMessageAt) return '';
    const date = new Date(conversation.lastMessageAt);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const isHe = i18n.language === 'he';

    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString(isHe ? 'he-IL' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  }, [conversation.lastMessageAt, i18n.language]);

  const lastMessagePreview = useMemo(() => {
    if (!conversation.lastMessage) return '';
    const lm = conversation.lastMessage;
    if (lm.type !== 'text') return `[${lm.type}]`;
    return lm.content || '';
  }, [conversation.lastMessage]);

  const [showDelete, setShowDelete] = useState(false);

  const isDm = conversation.type === 'dm';

  return (
    <div dir="ltr" className="group relative overflow-hidden" style={{ borderRadius: 20 }}>
      {/* Delete background revealed on swipe */}
      {showDelete && (
        <div className="absolute inset-0 flex items-center justify-end bg-red-500 pe-4" style={{ borderRadius: 20 }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(conversation.id);
            }}
            className="flex items-center gap-2 font-bold text-white"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            {t('common.delete')}
          </button>
        </div>
      )}
      <motion.button
        type="button"
        onClick={onClick}
        whileTap={{ scale: 0.98 }}
        drag="x"
        dragConstraints={{ left: -100, right: 0 }}
        dragElastic={0.1}
        onDrag={(_, info) => {
          setShowDelete(info.offset.x < -50);
        }}
        onDragEnd={(_, info) => {
          if (info.offset.x < -100) {
            onDelete?.(conversation.id);
          }
          setShowDelete(false);
        }}
        className="dock-icon-glass relative flex w-full items-center gap-3 p-4 text-start"
        style={{ borderRadius: 20 }}
      >
        {/* Avatar with online indicator */}
        <div className="relative flex-shrink-0">
          <ChatAvatar
            src={avatar}
            name={isDm ? name : '#'}
            size="h-12 w-12"
            textSize="text-lg"
          />
          {isDm && isOnline && (
            <div className="absolute -bottom-0.5 -end-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden" dir="auto">
          <div className="flex items-center justify-between gap-2">
            <span className={`truncate text-[15px] ${hasUnread ? 'font-bold' : 'font-medium'}`} style={{ color: 'var(--user-page-heading)' }}>
              {name}
            </span>
            <span className="flex-shrink-0 text-[11px] font-medium tabular-nums" style={{ color: hasUnread ? 'var(--user-page-accent)' : 'var(--user-page-text)' }}>
              {timeLabel}
            </span>
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            {typingUsers.length > 0 ? (
              <span className="flex items-center gap-1.5 truncate text-sm font-medium text-emerald-600">
                <span className="flex items-center gap-0.5">
                  <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '600ms' }} />
                  <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms', animationDuration: '600ms' }} />
                  <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms', animationDuration: '600ms' }} />
                </span>
                {typingUsers.length === 1
                  ? t('messaging.typingSingle', { name: typingUsers[0].userName })
                  : t('messaging.typingMultiple', { count: typingUsers.length })}
              </span>
            ) : (
              <span className={`truncate text-sm ${hasUnread ? 'font-medium' : ''}`} style={{ color: hasUnread ? 'var(--user-page-accent)' : 'var(--user-page-text)' }}>
                {conversation.lastMessage && !isDm && (
                  <span className="font-medium">{conversation.lastMessage.senderName}: </span>
                )}
                {lastMessagePreview}
              </span>
            )}
            {hasUnread && (
              <span
                className="flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold text-white"
                style={{ background: 'var(--user-page-accent)' }}
              >
                {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
              </span>
            )}
          </div>
        </div>
      </motion.button>
    </div>
  );
});

// =============================================================================
// Mobile Channel Item — Brand-colored card design
// =============================================================================

interface ChannelItemProps {
  channel: Channel;
  onClick: () => void;
}

const ChannelItem = memo(function ChannelItem({ channel, onClick }: ChannelItemProps) {
  const { t } = useTranslation();
  const typingUsers = useConversationTyping(channel.id);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      className="dock-icon-glass flex w-full items-center gap-3 p-4 text-start"
      style={{ borderRadius: 20 }}
    >
      {/* Channel avatar */}
      <div
        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-lg font-semibold"
        style={{ background: 'color-mix(in srgb, var(--user-page-accent) 10%, transparent)', color: 'var(--user-page-accent)' }}
      >
        #
      </div>

      <div className="flex-1 overflow-hidden" dir="auto">
        <div className="flex items-center gap-2">
          <span className="truncate text-[15px] font-medium" style={{ color: 'var(--user-page-heading)' }}>{channel.name}</span>
          {channel.isJoined && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
              style={{ background: 'color-mix(in srgb, var(--user-page-accent) 8%, transparent)', color: 'var(--user-page-accent)' }}
            >
              {t('messaging.joined')}
            </span>
          )}
        </div>
        {typingUsers.length > 0 ? (
          <span className="mt-0.5 flex items-center gap-1 truncate text-sm font-medium text-emerald-600">
            <span className="flex items-center gap-0.5">
              <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '600ms' }} />
              <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms', animationDuration: '600ms' }} />
              <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms', animationDuration: '600ms' }} />
            </span>
            {typingUsers.length === 1
              ? t('messaging.typingSingle', { name: typingUsers[0].userName })
              : t('messaging.typingMultiple', { count: typingUsers.length })}
          </span>
        ) : (
          <>
            {channel.description && (
              <span className="mt-0.5 block truncate text-sm" style={{ color: 'var(--user-page-text)' }}>
                {channel.description}
              </span>
            )}
            <span className="text-[11px]" style={{ color: 'var(--user-page-text)' }}>
              {t('messaging.members', { count: channel.memberCount })}
            </span>
          </>
        )}
      </div>
    </motion.button>
  );
});

// =============================================================================
// Shared hooks — data fetching used by both views
// =============================================================================

type Tab = 'chats' | 'channels';

function useMessagesData() {
  const currentUserId = useAuthStore((s) => s.user?.id) || '';
  const [activeTab, setActiveTab] = useState<Tab>('chats');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: conversations, isLoading: conversationsLoading } = useConversations();
  const { data: channels, isLoading: channelsLoading } = useChannels();
  const leaveConversation = useLeaveConversation();
  const markAllAsRead = useMarkAllAsRead();
  const totalUnread = useTotalUnreadCount();

  const dmPartnerIds = useMemo(() => {
    if (!conversations) return [];
    const ids: string[] = [];
    for (const c of conversations) {
      if (c.type === 'dm') {
        const partner = c.participants.find((p) => p.userId !== currentUserId);
        if (partner) ids.push(partner.userId);
      }
    }
    return ids;
  }, [conversations, currentUserId]);

  const { presenceMap } = useBatchPresence(dmPartnerIds);

  const handleDeleteConversation = useCallback(
    (conversationId: string) => leaveConversation.mutate(conversationId),
    [leaveConversation],
  );

  const handleMarkAllAsRead = useCallback(() => markAllAsRead.mutate(), [markAllAsRead]);

  const filteredConversations = useMemo(() => {
    if (!conversations) return [];
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter((c) =>
      getConversationDisplayName(c, currentUserId).toLowerCase().includes(q),
    );
  }, [conversations, searchQuery, currentUserId]);

  const filteredChannels = useMemo(() => {
    if (!channels) return [];
    if (!searchQuery.trim()) return channels;
    const q = searchQuery.toLowerCase();
    return channels.filter(
      (ch) => ch.name.toLowerCase().includes(q) || ch.description?.toLowerCase().includes(q),
    );
  }, [channels, searchQuery]);

  const isLoading = activeTab === 'chats' ? conversationsLoading : channelsLoading;
  const isEmpty =
    activeTab === 'chats'
      ? filteredConversations.length === 0
      : filteredChannels.length === 0;

  return {
    currentUserId,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    filteredConversations,
    filteredChannels,
    presenceMap,
    totalUnread,
    isLoading,
    isEmpty,
    handleDeleteConversation,
    handleMarkAllAsRead,
  };
}

// =============================================================================
// Mobile Messages View — Regular page, uses navigate()
// =============================================================================

function MobileMessagesView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    currentUserId,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    filteredConversations,
    filteredChannels,
    presenceMap,
    totalUnread,
    isLoading,
    isEmpty,
    handleDeleteConversation,
    handleMarkAllAsRead,
  } = useMessagesData();

  const handleConversationClick = useCallback(
    (id: string) => {
      setPendingPush('ChatActivity', { conversationId: id });
      navigate(`/messages/${id}`);
    },
    [navigate],
  );

  const handleChannelClick = useCallback(
    async (id: string) => {
      // Auto-join if not yet a member
      const ch = filteredChannels.find((c) => c.id === id);
      if (ch && !ch.isJoined) {
        try { await messagingApi.joinChannel(id); } catch { /* ignore, will 403 below if truly unauthorized */ }
      }
      setPendingPush('ChatActivity', { conversationId: id });
      navigate(`/messages/${id}`);
    },
    [navigate, filteredChannels],
  );

  return (
    <div
      className="-mx-4 sm:-mx-6 md:-mx-8 min-h-[calc(100dvh-4rem)]"
      style={{ backgroundColor: 'var(--user-page-bg)', marginTop: '-2rem' }}
    >
      {/* ─── Hero header with frosted glass ─── */}
      <div
        className="px-4 pb-5"
        style={{
          paddingTop: 'calc(2rem + 0.75rem)',
          backgroundColor: 'var(--user-page-card-bg)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '2px solid var(--user-page-card-border)',
          boxShadow: '0 2px 8px color-mix(in srgb, var(--user-page-text) 8%, transparent)',
        }}
      >
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-extrabold" style={{ color: 'var(--user-page-heading)' }}>{t('messaging.title')}</h1>
          <div className="flex items-center gap-2">
            {/* Starred messages */}
            <button
              type="button"
              onClick={() => {
                setPendingPush('StarredMessagesActivity', {});
                navigate('/messages/starred');
              }}
              className="flex h-9 w-9 items-center justify-center transition-colors"
              style={{
                borderRadius: 12,
                background: 'color-mix(in srgb, var(--user-page-text) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--user-page-text) 8%, transparent)',
              }}
              title={t('messaging.starredMessages')}
            >
              <svg className="h-4.5 w-4.5" style={{ color: 'var(--user-page-heading)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </button>
            {totalUnread > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="flex h-9 w-9 items-center justify-center transition-colors"
                style={{
                  borderRadius: 12,
                  background: 'color-mix(in srgb, var(--user-page-text) 8%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--user-page-text) 8%, transparent)',
                }}
                title={t('messaging.markAllAsRead')}
              >
                <svg className="h-4.5 w-4.5" style={{ color: 'var(--user-page-heading)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setPendingPush('NewConversationActivity', {});
                navigate('/messages/new');
              }}
              className="flex h-9 w-9 items-center justify-center transition-colors"
              style={{
                borderRadius: 12,
                background: 'color-mix(in srgb, var(--user-page-text) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--user-page-text) 8%, transparent)',
              }}
            >
              <svg className="h-4.5 w-4.5" style={{ color: 'var(--user-page-heading)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search bar — frosted glass */}
        <div className="relative mt-3">
          <svg
            className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2"
            style={{ color: 'var(--user-page-text)' }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('messaging.search')}
            dir="auto"
            className="messages-search w-full py-2.5 pe-4 ps-10 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-black/10"
            style={{
              borderRadius: 14,
              background: 'color-mix(in srgb, var(--user-page-text) 8%, transparent)',
              border: '1px solid color-mix(in srgb, var(--user-page-text) 8%, transparent)',
              color: 'var(--user-page-heading)',
            }}
          />
          <style>{`.messages-search::placeholder { color: var(--user-page-text) !important; opacity: 0.6; }`}</style>
        </div>

        {/* Tab switcher — glass segmented control */}
        <div
          className="mt-3 flex p-1"
          style={{
            borderRadius: 14,
            background: 'color-mix(in srgb, var(--user-page-text) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--user-page-text) 8%, transparent)',
          }}
        >
          {(['chats', 'channels'] as Tab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className="flex-1 py-2 text-sm transition-all"
              style={{
                borderRadius: 10,
                ...(activeTab === tab
                  ? {
                      background: 'var(--user-page-card-bg)',
                      fontWeight: 700,
                      color: 'var(--user-page-heading)',
                      boxShadow: '0 2px 8px color-mix(in srgb, var(--user-page-text) 8%, transparent)',
                    }
                  : {
                      fontWeight: 500,
                      color: 'var(--user-page-text)',
                    }),
              }}
            >
              {t(`messaging.${tab}`)}
            </button>
          ))}
        </div>
      </div>

      {/* ─── List area ─── */}
      <div
        className="px-4 pt-4 pb-4 flex-1"
        style={{
          backgroundColor: 'var(--user-page-card-bg)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-[76px] animate-pulse"
                style={{ borderRadius: 20, background: 'color-mix(in srgb, var(--user-page-accent) 8%, transparent)' }}
              />
            ))}
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{
                background: 'var(--user-page-card-bg)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid var(--user-page-card-border)',
                boxShadow: '0 4px 16px color-mix(in srgb, var(--user-page-text) 8%, transparent)',
              }}
            >
              <svg className="h-7 w-7" style={{ color: 'var(--user-page-accent)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
            </div>
            <p className="text-sm font-medium" style={{ color: 'var(--user-page-text)' }}>
              {searchQuery
                ? t('messaging.noResults')
                : activeTab === 'chats'
                  ? t('messaging.noConversations')
                  : t('messaging.noChannels')}
            </p>
            {!searchQuery && activeTab === 'chats' && (
              <button
                type="button"
                onClick={() => {
                  setPendingPush('NewConversationActivity', {});
                  navigate('/messages/new');
                }}
                className="dock-icon-glass mt-4 px-5 py-2.5 text-sm font-semibold"
                style={{ borderRadius: 16, color: 'var(--user-page-accent)' }}
              >
                {t('messaging.startConversation')}
              </button>
            )}
            {!searchQuery && activeTab === 'channels' && (
              <button
                type="button"
                onClick={() => {
                  setPendingPush('NewChannelActivity', {});
                  navigate('/messages/new-channel');
                }}
                className="dock-icon-glass mt-4 px-5 py-2.5 text-sm font-semibold"
                style={{ borderRadius: 16, color: 'var(--user-page-accent)' }}
              >
                {t('messaging.channel.createNew')}
              </button>
            )}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              className="space-y-2.5"
            >
              {activeTab === 'chats' &&
                filteredConversations.map((conv) => {
                  const partnerId = conv.type === 'dm'
                    ? conv.participants.find((p) => p.userId !== currentUserId)?.userId
                    : undefined;
                  const presence = partnerId ? presenceMap[partnerId] : undefined;
                  return (
                    <ConversationItem
                      key={conv.id}
                      conversation={conv}
                      currentUserId={currentUserId}
                      isOnline={presence?.isOnline ?? false}
                      onClick={() => handleConversationClick(conv.id)}
                      onDelete={handleDeleteConversation}
                    />
                  );
                })}
              {activeTab === 'channels' && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setPendingPush('NewChannelActivity', {});
                      navigate('/messages/new-channel');
                    }}
                    className="flex w-full items-center gap-3 p-4 text-start transition-all"
                    style={{
                      borderRadius: 20,
                      border: '2px dashed color-mix(in srgb, var(--user-page-accent) 20%, transparent)',
                      background: 'var(--user-page-card-bg)',
                    }}
                  >
                    <div
                      className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full"
                      style={{ background: 'color-mix(in srgb, var(--user-page-accent) 8%, transparent)', color: 'var(--user-page-accent)' }}
                    >
                      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--user-page-accent)' }}>
                      {t('messaging.channel.createNew')}
                    </span>
                  </button>
                  {filteredChannels.map((ch) => (
                    <ChannelItem
                      key={ch.id}
                      channel={ch}
                      onClick={() => handleChannelClick(ch.id)}
                    />
                  ))}
                </>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Desktop Messages View — Welcome placeholder (Stackflow left pane handles list)
// =============================================================================

function DesktopMessagesView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const { push, replace } = useMessagingFlow();
  const activity = useActivity();
  const wasHidden = useRef(false);

  // Use layoutEffect to flush pending push before paint (avoids welcome screen flash)
  useLayoutEffect(() => {
    registerMessagingFlow({ push, replace });
    return () => unregisterMessagingFlow();
  }, [push, replace]);

  // Track when this activity is pushed behind ChatActivity (desktop only).
  // On mobile, SpatialOverlay is the sole navigation authority — these
  // effects must not fire or they race with the gesture-driven navigate.
  useEffect(() => {
    if (isMobile) return;
    if (!activity.isTop) {
      wasHidden.current = true;
    }
  }, [isMobile, activity.isTop]);

  // Desktop only: when this activity becomes top again after a pop,
  // navigate to /messages to sync the URL
  useEffect(() => {
    if (isMobile) return;
    if (activity.isTop && wasHidden.current) {
      wasHidden.current = false;
      navigate('/messages', { replace: true });
    }
  }, [isMobile, activity.isTop, navigate]);

  // On mobile: never render content — this activity is just a bridge for
  // pending push and back-navigation detection. Keeps the Stackflow swipe-back
  // transparent (no welcome screen flash behind ChatActivity).
  if (isMobile) return null;

  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full" style={{ backgroundColor: 'var(--user-page-card-bg)' }}>
        <svg className="h-10 w-10" style={{ color: 'var(--user-page-text)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
      </div>
      <h2 className="text-xl font-bold" style={{ color: 'var(--user-page-heading)' }}>{t('messaging.title')}</h2>
      <p className="mt-2 text-sm font-medium" style={{ color: 'var(--user-page-text)' }}>{t('messaging.welcomeMessage')}</p>
    </div>
  );
}

// =============================================================================
// Exported component — routes to correct view based on breakpoint
// =============================================================================

export function MessagesPage() {
  return <MobileMessagesView />;
}

export { DesktopMessagesView };

export default MessagesPage;
